import { initReactI18next } from "react-i18next";
import { languageCookie, resolveLanguage } from "./cookie.js";
import { fallbackChain, matchLanguage } from "./languages.js";
import { i18n } from "./instance.js";
const registered = new Map();
function register(namespaces) {
    for (const namespace of namespaces)
        registered.set(namespace.name, namespace);
}
/** Loads every registered namespace's texts for the tag's chain, except English, which is bundled. */
async function loadChain(tag) {
    const loads = fallbackChain(tag)
        .filter((language) => language !== "en")
        .flatMap((language) => [...registered.values()].flatMap((namespace) => {
        const loader = namespace.loaders[language];
        if (!loader || (i18n.isInitialized && i18n.hasResourceBundle(language, namespace.name)))
            return [];
        return [loader().then((module) => ({ language, namespace: namespace.name, texts: module.default }))];
    }));
    return Promise.all(loads);
}
async function init(language, namespaces, bundles = []) {
    register(namespaces);
    const resources = {
        en: Object.fromEntries(namespaces.map((namespace) => [namespace.name, namespace.en])),
    };
    for (const { language: tag, namespace, texts } of bundles)
        (resources[tag] ??= {})[namespace] = texts;
    await i18n.use(initReactI18next).init({
        lng: language,
        fallbackLng: (code) => fallbackChain(code),
        ns: namespaces.map((namespace) => namespace.name),
        defaultNS: false,
        resources,
        initAsync: false,
        // React escapes what it renders.
        interpolation: { escapeValue: false },
        react: { useSuspense: false },
    });
}
function setDocumentLanguage(tag) {
    if (typeof document !== "undefined")
        document.documentElement.lang = tag;
}
/**
 * Resolves the UI language and loads its texts, before the first render. Never rejects: when the
 * texts can't be loaded, the UI is English.
 */
export async function setupI18n({ namespaces, language = resolveLanguage(), setDocumentLang = true }) {
    if (i18n.isInitialized) {
        // A hot-module reload re-runs the app's entry; keep the language the page already shows.
        addEnglish(namespaces);
        return;
    }
    register(namespaces);
    let resolved = language;
    let bundles = [];
    try {
        bundles = await loadChain(language);
    }
    catch (error) {
        console.error(`Loading the "${language}" texts failed; showing English`, error);
        resolved = "en";
    }
    await init(resolved, namespaces, bundles);
    if (setDocumentLang)
        setDocumentLanguage(resolved);
}
function addEnglish(namespaces) {
    register(namespaces);
    for (const namespace of namespaces)
        i18n.addResourceBundle("en", namespace.name, namespace.en, true, true);
}
/** English only and synchronous, so tests render today's English copy without loading anything. */
export function setupI18nForTests(namespaces) {
    if (i18n.isInitialized) {
        addEnglish(namespaces);
        void i18n.changeLanguage("en");
        return;
    }
    void init("en", namespaces);
}
let latestChoice = 0;
/**
 * Switches the UI to a language the user chose and remembers it in the `tikl` cookie. The texts
 * load before the switch, so no frame mixes two languages. Returns false when they can't be loaded,
 * or when a later choice has superseded this one while it loaded.
 */
export async function chooseLanguage(tag) {
    const language = matchLanguage(tag);
    if (!language)
        return false;
    const choice = ++latestChoice;
    try {
        for (const bundle of await loadChain(language)) {
            i18n.addResourceBundle(bundle.language, bundle.namespace, bundle.texts, true, true);
        }
    }
    catch (error) {
        console.error(`Loading the "${language}" texts failed; keeping "${i18n.language}"`, error);
        return false;
    }
    if (choice !== latestChoice)
        return false;
    await i18n.changeLanguage(language);
    setDocumentLanguage(language);
    document.cookie = languageCookie(language, window.location);
    return true;
}
//# sourceMappingURL=setup.js.map